// Import polyfills first so they're available before any other code runs
import { preloadBufferPolyfill } from './polyfills'

import React from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import Futuur from './Futuur'
import { makeStore } from './store'
import preventZoom from './util/preventZoom'
import orientationLock from './util/orientationLock'
import { initIOSKeyboardFix } from './util/iosKeyboardFix'
import './styles/main.scss'
import './index.css'
// Footer is lazy on the client but server-rendered on every SSR page, so its styles ship with the app CSS.
import './layouts/Footer/Footer.scss'

// Dev serves CSS through these imports rather than a <link>, so it is applied once they have run.
if (import.meta.env.DEV) {
  document.documentElement.classList.add('css-ready')
}

if (typeof window !== 'undefined') {
  preloadBufferPolyfill()
  orientationLock('portrait')
  preventZoom()
  initIOSKeyboardFix()
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element #root not found')

const clientStore =
  typeof window !== 'undefined' && window.__PRELOADED_STATE__
    ? makeStore(window.__PRELOADED_STATE__)
    : null

const app = (
  <React.StrictMode>
    <Futuur store={clientStore} />
  </React.StrictMode>
)

// Partial SSR (e.g. category nav only): server sent only a fragment, so replace with full app (createRoot).
// Full SSR: server sent full app, so hydrate.
// No SSR: the server strips the <!--ssr-outlet--> marker and leaves #root empty,
// so "marker absent" alone doesn't imply server markup — the root must also be
// non-empty, else hydrateRoot runs against empty HTML and every node mismatches.
const isPartialSSR = rootElement.getAttribute('data-ssr') === 'partial'
const hasServerMarkup =
  rootElement.firstElementChild != null && !rootElement.innerHTML.includes('<!--ssr-outlet-->')
  if (isPartialSSR || !hasServerMarkup) {
  createRoot(rootElement).render(app)
  if (isPartialSSR) {
    // Match server-injected #root { opacity: 0 } until styles + first paint settle, then reveal.
    const reveal = () => {
      rootElement.classList.add('ssr-ready')
      document.getElementById('ssr-anti-fouc')?.remove()
    }
    // Two rAFs: wait for layout + paint after hydrate/createRoot. No fixed timeout —
    // a blank 250ms delay was padding every SSR route with no FOUC benefit.
    requestAnimationFrame(() => {
      requestAnimationFrame(reveal)
    })
  }
} else {
  hydrateRoot(rootElement, app)
}
